import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { activityApi } from "../../api/activity";
import { billingAccessLabel, getProjectBillingAccess } from "../../utils/projectBillingAccess";
import {
  isProjectCapabilityEnabled,
  projectBusinessGoals,
  projectStarterAreas,
} from "../../utils/projectCapabilities";
import { getProjectPublicUrl } from "../../utils/projectDeployment";
import { buildProjectActionTasks } from "../../utils/projectActionTasks";
import { getProjectExperience } from "../../utils/projectExperience";
import { normalizeProjectModules } from "../../utils/projectModules";

const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  purple: "#6957E8",
  green: "#26A269",
  blue: "#2878D4",
  yellow: "#FFC857",
};

const COLOR_MAP = {
  orange: { main: BRAND.orange, soft: "rgba(243,90,58,.11)", border: "rgba(243,90,58,.24)" },
  purple: { main: BRAND.purple, soft: "rgba(105,87,232,.10)", border: "rgba(105,87,232,.22)" },
  green: { main: BRAND.green, soft: "rgba(38,162,105,.10)", border: "rgba(38,162,105,.22)" },
  blue: { main: BRAND.blue, soft: "rgba(40,120,212,.10)", border: "rgba(40,120,212,.22)" },
  yellow: { main: "#9A6500", soft: "rgba(255,200,87,.18)", border: "rgba(154,101,0,.2)" },
  dark: { main: BRAND.dark, soft: "rgba(25,36,47,.08)", border: "rgba(25,36,47,.16)" },
};

function ActionTile({
  icon,
  title,
  description,
  label = "Abrir",
  color = "orange",
  disabled = false,
  badge,
  featured = false,
  onClick,
}) {
  const palette = COLOR_MAP[color] || COLOR_MAP.orange;

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`${title}: ${disabled ? "ver acceso" : label}`}
      sx={{
        width: "100%",
        height: "100%",
        display: "block",
        textAlign: "left",
        borderRadius: { xs: 2.5, sm: 3.4 },
        overflow: "hidden",
        "&:focus-visible": { boxShadow: `0 0 0 4px ${palette.soft}` },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          minHeight: featured ? { xs: 124, sm: 168 } : { xs: 104, sm: 142 },
          borderRadius: { xs: 2.5, sm: 3.4 },
          border: `1px solid ${disabled ? "rgba(25,36,47,.1)" : palette.border}`,
          background: featured && !disabled
            ? `linear-gradient(145deg, #fff 0%, ${palette.soft} 100%)`
            : disabled
              ? "rgba(25,36,47,.025)"
              : "#fff",
          p: featured ? { xs: 1.45, sm: 2.2 } : { xs: 1.2, sm: 1.75 },
          opacity: disabled ? .78 : 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 15px 34px rgba(25,36,47,.1)",
            borderColor: disabled ? "rgba(25,36,47,.2)" : palette.main,
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box
            sx={{
              width: featured ? { xs: 40, sm: 48 } : { xs: 36, sm: 43 },
              height: featured ? { xs: 40, sm: 48 } : { xs: 36, sm: 43 },
              borderRadius: { xs: 2.1, sm: 2.7 },
              display: "grid",
              placeItems: "center",
              color: disabled ? "rgba(25,36,47,.5)" : palette.main,
              bgcolor: disabled ? "rgba(25,36,47,.07)" : palette.soft,
              flexShrink: 0,
              "& svg": { fontSize: featured ? { xs: 22, sm: 26 } : { xs: 20, sm: 23 } },
            }}
          >
            {disabled ? <LockClockRoundedIcon /> : icon}
          </Box>

          {badge ? (
            <Chip
              size="small"
              label={badge}
              sx={{
                height: 22,
                maxWidth: "62%",
                fontSize: ".61rem",
                fontWeight: 950,
                bgcolor: disabled ? "rgba(25,36,47,.06)" : palette.soft,
                color: disabled ? "rgba(25,36,47,.58)" : palette.main,
              }}
            />
          ) : (
            <ArrowForwardRoundedIcon sx={{ color: "rgba(25,36,47,.28)", fontSize: { xs: 18, sm: 21 } }} />
          )}
        </Stack>

        <Box sx={{ minWidth: 0, mt: { xs: .9, sm: 1.35 } }}>
          <Typography
            sx={{
              color: BRAND.dark,
              fontSize: featured ? { xs: ".93rem", sm: "1.08rem" } : { xs: ".79rem", sm: ".96rem" },
              lineHeight: 1.14,
              fontWeight: 1000,
              letterSpacing: "-.15px",
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              display: { xs: featured ? "block" : "none", sm: "block" },
              mt: .48,
              color: "rgba(25,36,47,.6)",
              fontSize: featured ? { xs: ".69rem", sm: ".8rem" } : ".75rem",
              lineHeight: 1.36,
            }}
          >
            {description}
          </Typography>
          <Typography
            sx={{
              mt: { xs: .58, sm: .75 },
              color: disabled ? "rgba(25,36,47,.52)" : palette.main,
              fontSize: { xs: ".65rem", sm: ".72rem" },
              fontWeight: 1000,
            }}
          >
            {disabled ? "Ver acceso" : label}
          </Typography>
        </Box>
      </Paper>
    </ButtonBase>
  );
}

function projectIdentifier(project) {
  return project?.slug || project?.id || project?._id || "";
}

function relativeTime(value) {
  const timestamp = new Date(value || 0).getTime();
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "";
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60000));
  if (minutes < 1) return "Ahora";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} d`;
}

function DashboardMetric({ label, value, helper, icon }) {
  return (
    <Paper elevation={0} sx={{ height: "100%", p: { xs: 1.2, sm: 1.6 }, borderRadius: 3, border: "1px solid rgba(25,36,47,.08)", bgcolor: "#fff" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "rgba(25,36,47,.52)", fontSize: ".68rem", fontWeight: 900 }}>{label}</Typography>
          <Typography sx={{ mt: .25, color: BRAND.dark, fontSize: { xs: "1.22rem", sm: "1.5rem" }, lineHeight: 1, fontWeight: 1000 }}>{value}</Typography>
          <Typography sx={{ mt: .45, color: "rgba(25,36,47,.5)", fontSize: ".67rem", lineHeight: 1.3 }}>{helper}</Typography>
        </Box>
        <Box sx={{ width: 34, height: 34, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(105,87,232,.08)", color: BRAND.purple, flexShrink: 0 }}>
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function ClientCommandCenter({
  user,
  projects,
  activeProject,
  setActiveProject,
  projectCreation,
  loading,
  error,
}) {
  const navigate = useNavigate();
  const [showAllTools, setShowAllTools] = React.useState(false);
  const [showGuidance, setShowGuidance] = React.useState(false);
  const [readiness, setReadiness] = React.useState(null);
  const [readinessLoading, setReadinessLoading] = React.useState(false);
  const [activitySummary, setActivitySummary] = React.useState(null);
  const [recentActivity, setRecentActivity] = React.useState([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const modules = normalizeProjectModules(activeProject?.modules);
  const billingAccess = getProjectBillingAccess(activeProject);
  const projectKey = projectIdentifier(activeProject);
  const encodedProjectKey = encodeURIComponent(projectKey);
  const projectBase = projectKey
    ? `/dashboard/proyectos/${encodedProjectKey}`
    : "/dashboard/proyectos";
  const publicUrl = getProjectPublicUrl(activeProject);
  const operationallyLocked = billingAccess.isLandingOnly || billingAccess.isSuspended;
  const membershipsEnabled = isProjectCapabilityEnabled(activeProject, "memberships");
  const itemPath = modules.system ? `${projectBase}/sistema/items` : `${projectBase}/productos`;
  const subscribePath = `${projectBase}/configuracion/suscripcion`;
  const experience = getProjectExperience(activeProject, readiness ?? activeProject?.readiness);
  const guidedStart = experience.intensiveOnboarding;
  const businessGoals = projectBusinessGoals(activeProject);
  const starterAreas = projectStarterAreas(activeProject);
  const tablesEnabled = isProjectCapabilityEnabled(activeProject, "tables");
  const tableReservationsEnabled = isProjectCapabilityEnabled(activeProject, "tableReservations");
  const kitchenOrdersEnabled = isProjectCapabilityEnabled(activeProject, "kitchenOrders");
  const gastronomicMenuEnabled = isProjectCapabilityEnabled(activeProject, "gastronomicMenu");
  const recipesEnabled = isProjectCapabilityEnabled(activeProject, "recipes");

  React.useEffect(() => {
    setShowAllTools(false);
    setShowGuidance(false);
  }, [projectKey]);

  React.useEffect(() => {
    let cancelled = false;
    const fallback = activeProject?.readiness || null;
    setReadiness(fallback);
    setActivitySummary(null);
    setRecentActivity([]);

    if (!projectKey) {
      setReadinessLoading(false);
      setActivityLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setReadinessLoading(true);
    setActivityLoading(true);

    Promise.allSettled([
      projectsApi.getReadiness(projectKey),
      activityApi.projectSummary(projectKey, 168),
      activityApi.project(projectKey, { page: 1, pageSize: 5 }),
    ]).then(([readinessResult, summaryResult, activityResult]) => {
      if (cancelled) return;

      setReadiness(
        readinessResult.status === "fulfilled"
          ? readinessResult.value?.readiness || fallback
          : fallback,
      );
      setActivitySummary(
        summaryResult.status === "fulfilled"
          ? summaryResult.value?.summary || null
          : null,
      );
      setRecentActivity(
        activityResult.status === "fulfilled" && Array.isArray(activityResult.value?.items)
          ? activityResult.value.items
          : [],
      );
    }).finally(() => {
      if (!cancelled) {
        setReadinessLoading(false);
        setActivityLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [projectKey, activeProject?.readiness?.computedAt]);

  const go = React.useCallback((path, { operational = false } = {}) => {
    if (!projectKey) {
      navigate("/dashboard/proyectos");
      return;
    }
    if (operational && operationallyLocked) {
      navigate(subscribePath);
      return;
    }
    navigate(path);
  }, [navigate, operationallyLocked, projectKey, subscribePath]);

  const openPublicSite = () => {
    if (publicUrl) {
      window.open(publicUrl, "_blank", "noopener,noreferrer");
      return;
    }
    go(`${projectBase}/pagina`);
  };

  if (loading && !activeProject) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 4, p: 3, border: "1px solid rgba(25,36,47,.08)" }}>
        <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>Preparando tu panel...</Typography>
        <LinearProgress sx={{ mt: 2, borderRadius: 99 }} />
      </Paper>
    );
  }

  if (!activeProject) {
    return (
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 2.25, sm: 3.5 }, border: "1px solid rgba(25,36,47,.08)" }}>
          <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 1000 }}>Elegí un proyecto para empezar</Typography>
          <Typography sx={{ mt: .75, color: "rgba(25,36,47,.62)" }}>Desde el inicio vas a poder editar tu web, cargar ítems y manejar la operación.</Typography>
          <Button
            variant="contained"
            startIcon={<StorefrontRoundedIcon />}
            onClick={() => navigate("/dashboard/proyectos")}
            sx={{ mt: 2, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 950 }}
          >
            {projectCreation?.canCreate ? "Crear proyecto" : "Ver proyectos"}
          </Button>
        </Paper>
      </Stack>
    );
  }

  const membershipActions = membershipsEnabled ? [
    {
      key: "membership-customer",
      icon: <PersonAddAlt1RoundedIcon />,
      title: "Crear cliente",
      description: "Alta rápida en CRM, sin obligar a crear contraseña ni acceso web.",
      label: "Nuevo cliente",
      color: "orange",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes?accion=crear-cliente`, { operational: true }),
    },
    {
      key: "membership-assign",
      icon: <CardMembershipRoundedIcon />,
      title: "Asignar plan",
      description: "Buscá un cliente y abrí su ficha para asignarle un servicio o plan.",
      label: "Ir al CRM",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes?accion=asignar-plan`, { operational: true }),
    },
    {
      key: "membership-renew",
      icon: <AutorenewRoundedIcon />,
      title: "Renovar membresía",
      description: "Accedé a los períodos que requieren continuidad sin perder historial.",
      label: "Revisar renovaciones",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes?accion=renovar`, { operational: true }),
    },
    {
      key: "membership-expirations",
      icon: <NotificationsActiveRoundedIcon />,
      title: "Revisar vencimientos",
      description: "Priorizá próximas a vencer y vencidas desde el seguimiento del CRM.",
      label: "Ver vencimientos",
      color: "yellow",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes?accion=vencimientos`, { operational: true }),
    },
  ] : [];

  const primaryActions = [
    {
      key: "landing",
      visible: modules.landing,
      icon: <DesignServicesRoundedIcon />,
      title: "Editar página",
      description: "Cambiá portada, textos, imágenes, colores y datos de contacto.",
      label: "Abrir editor",
      color: "orange",
      onClick: () => go(`${projectBase}/pagina`),
    },
    {
      key: "product",
      visible: modules.products || modules.system,
      icon: <Inventory2RoundedIcon />,
      title: "Cargar producto",
      description: "Creá un artículo con precio, imágenes y stock.",
      label: "Nuevo producto",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${itemPath}?crear=1&tipo=product`, { operational: true }),
    },
    {
      key: "service",
      visible: modules.products || modules.system,
      icon: <AddBusinessRoundedIcon />,
      title: "Cargar servicio",
      description: "Publicá una consulta, turno o trabajo con duración y modalidad.",
      label: "Nuevo servicio",
      color: "blue",
      disabled: operationallyLocked,
      onClick: () => go(`${itemPath}?crear=1&tipo=service`, { operational: true }),
    },
    {
      key: "movement",
      visible: modules.system,
      icon: <PointOfSaleRoundedIcon />,
      title: "Nueva operación",
      description: "Registrá una venta, compra, cobro, pago, ingreso o gasto.",
      label: "Generar operación",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/sistema/movimientos?nuevo=1`, { operational: true }),
    },
  ].filter((action) => action.visible);

  const serviceFirst =
    businessGoals.includes("offerServices") ||
    (["services", "gym"].includes(activeProject?.businessProfile) && !businessGoals.includes("sellProducts"));

  const guidedActionByArea = {
    landing: {
      key: "guided-landing",
      visible: modules.landing,
      icon: <DesignServicesRoundedIcon />,
      title: "Preparar mi página",
      description: "Completá los datos básicos y dejá lista la presentación del negocio.",
      label: "Editar página",
      color: "orange",
      onClick: () => go(`${projectBase}/pagina`),
    },
    items: {
      key: "guided-items",
      visible: modules.products || modules.system,
      icon: serviceFirst ? <AddBusinessRoundedIcon /> : <Inventory2RoundedIcon />,
      title: serviceFirst ? "Cargar un servicio" : "Cargar un producto",
      description: serviceFirst
        ? "Empezá por el servicio que querés ofrecer o agendar."
        : "Empezá por un artículo real con precio e información básica.",
      label: serviceFirst ? "Nuevo servicio" : "Nuevo producto",
      color: serviceFirst ? "blue" : "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${itemPath}?crear=1&tipo=${serviceFirst ? "service" : "product"}`, { operational: true }),
    },
    agenda: {
      key: "guided-agenda",
      visible: modules.products,
      icon: <EventAvailableRoundedIcon />,
      title: "Preparar agenda",
      description: "Definí disponibilidad y empezá a ordenar reservas o turnos.",
      label: "Abrir agenda",
      color: "blue",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/agenda`, { operational: true }),
    },
    orders: {
      key: "guided-orders",
      visible: modules.orders,
      icon: <ListAltRoundedIcon />,
      title: "Revisar pedidos",
      description: "Conocé el flujo que vas a usar cuando empiecen a llegar solicitudes.",
      label: "Abrir pedidos",
      color: "orange",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/pedidos`, { operational: true }),
    },
    customers: {
      key: "guided-customers",
      visible: true,
      icon: <GroupRoundedIcon />,
      title: membershipsEnabled ? "Preparar clientes y membresías" : "Preparar clientes",
      description: membershipsEnabled
        ? "Organizá clientes y dejá listo el seguimiento de sus planes."
        : "Centralizá contactos y relaciones del negocio desde el CRM.",
      label: "Abrir CRM",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes`, { operational: true }),
    },
    system: {
      key: "guided-system",
      visible: modules.system,
      icon: <WarehouseRoundedIcon />,
      title: "Preparar la gestión",
      description: "Entrá al centro operativo para caja, movimientos, stock y control.",
      label: "Abrir gestión",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/sistema`, { operational: true }),
    },
    stats: {
      key: "guided-stats",
      visible: modules.stats,
      icon: <AnalyticsRoundedIcon />,
      title: "Ver estadísticas",
      description: "Ubicá desde ahora dónde vas a revisar actividad y resultados.",
      label: "Abrir estadísticas",
      color: "yellow",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/estadisticas`, { operational: true }),
    },
  };

  const guidedActions = starterAreas
    .map((area) => guidedActionByArea[area])
    .filter((action) => action?.visible)
    .slice(0, 3);
  const featuredActions = guidedActions.length ? guidedActions : primaryActions;

  const readinessFacts = readiness?.facts && typeof readiness.facts === "object"
    ? readiness.facts
    : {};
  const readinessAvailable = Number.isFinite(Number(readiness?.requiredCount));
  const factPending = (code) => readinessFacts?.[code]?.required === true && readinessFacts?.[code]?.complete !== true;

  const operationalFact = readinessFacts?.operationalManagement || {};
  const storedOperationalTargets = operationalFact?.targets && typeof operationalFact.targets === "object"
    ? operationalFact.targets
    : null;
  const operationalTargets = storedOperationalTargets || {
    customers: membershipsEnabled || businessGoals.includes("manageCustomers") || businessGoals.includes("manageMemberships"),
    reservations: tableReservationsEnabled || businessGoals.includes("takeReservations"),
    gastronomy: gastronomicMenuEnabled || kitchenOrdersEnabled || tablesEnabled,
    system: businessGoals.includes("controlBusiness") || recipesEnabled,
  };
  const operationalEvidence = operationalFact?.evidence && typeof operationalFact.evidence === "object"
    ? operationalFact.evidence
    : {};
  const targetByGoal = {
    manageCustomers: "customers",
    manageMemberships: "customers",
    takeReservations: "reservations",
    gastronomicMenu: "gastronomy",
    controlBusiness: "system",
  };
  const operationalTargetPriority = Array.from(new Set([
    ...businessGoals.map((goal) => targetByGoal[goal]).filter(Boolean),
    "customers",
    "reservations",
    "gastronomy",
    "system",
  ]));
  const pendingOperationalTarget = operationalTargetPriority.find(
    (target) => operationalTargets?.[target] === true && operationalEvidence?.[target] !== true,
  );

  const operationalRoadmapAction = (() => {
    if (pendingOperationalTarget === "customers") {
      return {
        title: membershipsEnabled ? "Empezar a gestionar clientes" : "Registrar el primer cliente",
        description: membershipsEnabled
          ? "Abrí el CRM y empezá el seguimiento de clientes y membresías."
          : "Centralizá el primer contacto real para iniciar la gestión comercial.",
        label: "Abrir CRM",
        color: "purple",
        onClick: () => go(`${projectBase}/clientes`, { operational: true }),
      };
    }
    if (pendingOperationalTarget === "reservations") {
      const restaurantReservations = tableReservationsEnabled || tablesEnabled;
      return {
        title: restaurantReservations ? "Preparar mesas y reservas" : "Preparar la agenda",
        description: restaurantReservations
          ? "Configurá la operación del salón para empezar a recibir reservas."
          : "Definí disponibilidad para empezar a recibir turnos o reservas.",
        label: restaurantReservations ? "Abrir mesas" : "Abrir agenda",
        color: restaurantReservations ? "orange" : "blue",
        onClick: () => go(
          restaurantReservations ? `${projectBase}/mesas` : `${projectBase}/agenda`,
          { operational: true },
        ),
      };
    }
    if (pendingOperationalTarget === "gastronomy") {
      const prepareMenu = gastronomicMenuEnabled || !modules.orders;
      return {
        title: prepareMenu ? "Preparar la carta gastronómica" : "Empezar con pedidos y comandas",
        description: prepareMenu
          ? "Revisá los ítems que forman la carta y dejá una opción gastronómica operativa."
          : "Usá pedidos y comandas para poner en marcha el circuito del negocio.",
        label: prepareMenu ? "Abrir ítems" : "Abrir pedidos",
        color: prepareMenu ? "purple" : "orange",
        onClick: () => go(prepareMenu ? itemPath : `${projectBase}/pedidos`, { operational: true }),
      };
    }
    if (pendingOperationalTarget === "system") {
      return {
        title: "Registrar la primera operación",
        description: "Empezá a usar la gestión con un movimiento real del negocio.",
        label: "Nueva operación",
        color: "green",
        onClick: () => go(`${projectBase}/sistema/movimientos?nuevo=1`, { operational: true }),
      };
    }
    return {
      title: "Empezar a gestionar el negocio",
      description: "Abrí la herramienta operativa principal para registrar actividad real.",
      label: "Abrir gestión",
      color: "green",
      onClick: () => go(`${projectBase}/sistema`, { operational: true }),
    };
  })();

  const roadmapCandidates = [
    {
      code: "identity",
      title: "Completar identidad del negocio",
      description: "Revisá el nombre y los datos base que identifican este proyecto.",
      label: "Completar datos",
      color: "dark",
      onClick: () => go(`${projectBase}/configuracion/general`),
    },
    {
      code: "pageStarted",
      title: "Dar forma a la página",
      description: "Empezá por la portada, textos e imágenes principales del negocio.",
      label: "Editar página",
      color: "orange",
      onClick: () => go(`${projectBase}/pagina`),
    },
    {
      code: "firstItem",
      title: serviceFirst ? "Crear el primer servicio" : "Crear el primer producto",
      description: serviceFirst
        ? "Cargá un servicio real para poder mostrarlo, agendarlo o gestionarlo."
        : "Cargá un producto real con la información mínima para empezar.",
      label: serviceFirst ? "Nuevo servicio" : "Nuevo producto",
      color: serviceFirst ? "blue" : "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${itemPath}?crear=1&tipo=${serviceFirst ? "service" : "product"}`, { operational: true }),
    },
    {
      code: "contact",
      title: "Completar datos comerciales",
      description: "Sumá teléfono, WhatsApp, email o dirección para que puedan contactarte.",
      label: "Completar contacto",
      color: "blue",
      onClick: () => go(`${projectBase}/pagina`),
    },
    {
      code: "publicContent",
      title: "Preparar contenido público",
      description: "Dejá contenido propio del negocio listo para que la página no salga vacía.",
      label: "Preparar contenido",
      color: "purple",
      onClick: () => go(`${projectBase}/pagina`),
    },
    {
      code: "operationalManagement",
      disabled: operationallyLocked,
      ...operationalRoadmapAction,
    },
    {
      code: "paymentMethod",
      title: "Conectar un medio de cobro",
      description: modules.ecommerce
        ? "Vinculá Mercado Pago cuando el recorrido del negocio necesite cobrar online."
        : "Revisá el acceso a cobros online para completar este paso cuando corresponda.",
      label: modules.ecommerce ? "Configurar cobros" : "Ver acceso",
      color: "green",
      disabled: operationallyLocked || !modules.ecommerce,
      onClick: () => modules.ecommerce
        ? go(`${projectBase}/configuracion/ecommerce`, { operational: true })
        : go(subscribePath),
    },
    {
      code: "publication",
      title: "Publicar el negocio",
      description: "Cuando lo esencial esté listo, publicá la versión que verán tus clientes.",
      label: "Revisar publicación",
      color: "orange",
      onClick: () => go(`${projectBase}/pagina`),
    },
  ];

  const pendingRoadmapSteps = roadmapCandidates
    .filter((step) => factPending(step.code))
    .slice(0, 3);
  const roadmapComplete = readinessAvailable && pendingRoadmapSteps.length === 0 && Number(readiness?.requiredCount || 0) > 0;
  const operationalTasks = readinessAvailable
    ? buildProjectActionTasks({ project: activeProject, readiness, modules }).slice(0, 3)
    : [];
  const activityMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.total || 0);
  const visitorMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.uniqueVisitors || 0);
  const intentMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.highIntent || 0);
  const teamMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.uniquePanelUsers || 0);

  const managementActions = [
    {
      key: "items",
      visible: modules.products || modules.system,
      icon: <Inventory2RoundedIcon />,
      title: modules.system ? "Ítems del negocio" : "Productos y servicios",
      description: "Revisá, editá y organizá todo lo que ofrecés o utilizás.",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(itemPath, { operational: true }),
    },
    {
      key: "system",
      visible: modules.system,
      icon: <WarehouseRoundedIcon />,
      title: "Gestión del negocio",
      description: "Resumen, caja, movimientos, stock y contabilidad.",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/sistema`, { operational: true }),
    },
    {
      key: "agenda",
      visible: modules.products,
      icon: <EventAvailableRoundedIcon />,
      title: "Agenda y turnos",
      description: "Disponibilidad y reservas de servicios.",
      color: "blue",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/agenda`, { operational: true }),
    },
    {
      key: "orders",
      visible: modules.orders,
      icon: <ListAltRoundedIcon />,
      title: "Pedidos",
      description: "Solicitudes, entregas, retiros y estados de compra.",
      color: "orange",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/pedidos`, { operational: true }),
    },
    {
      key: "stats",
      visible: modules.stats,
      icon: <AnalyticsRoundedIcon />,
      title: "Estadísticas",
      description: "Visitas, recorridos, interés y conversiones.",
      color: "yellow",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/estadisticas`, { operational: true }),
    },
    {
      key: "activity",
      visible: true,
      icon: <HistoryRoundedIcon />,
      title: "Actividad",
      description: "Historial de acciones realizadas en el proyecto.",
      color: "dark",
      onClick: () => go(`${projectBase}/actividad`),
    },
    {
      key: "ecommerce",
      visible: modules.ecommerce,
      icon: <PaymentsRoundedIcon />,
      title: "Cobros online",
      description: "Mercado Pago y checkout de la tienda.",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/configuracion/ecommerce`, { operational: true }),
    },
    {
      key: "notifications",
      visible: modules.webpush,
      icon: <NotificationsActiveRoundedIcon />,
      title: "Notificaciones",
      description: "Avisos automáticos y campañas web push.",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/configuracion/notificaciones`, { operational: true }),
    },
    {
      key: "access",
      visible: true,
      icon: <GroupRoundedIcon />,
      title: "Equipo y accesos",
      description: "Personas, permisos y responsabilidades.",
      color: "dark",
      onClick: () => go(`${projectBase}/configuracion/accesos`),
    },
    {
      key: "settings",
      visible: true,
      icon: <SettingsRoundedIcon />,
      title: "Configuración",
      description: "Datos principales y estructura del proyecto.",
      color: "dark",
      onClick: () => go(`${projectBase}/configuracion/general`),
    },
    {
      key: "subscription",
      visible: true,
      icon: <WorkspacePremiumRoundedIcon />,
      title: "Plan y suscripción",
      description: "Módulos activos y estado del servicio.",
      color: "orange",
      onClick: () => go(subscribePath),
    },
    {
      key: "projects",
      visible: true,
      icon: <StorefrontRoundedIcon />,
      title: "Mis proyectos",
      description: "Cambiá de proyecto o creá uno nuevo.",
      color: "dark",
      onClick: () => navigate("/dashboard/proyectos"),
    },
    {
      key: "public",
      visible: modules.landing,
      icon: <PublicRoundedIcon />,
      title: "Ver página pública",
      description: publicUrl
        ? "Abrí la página que ya pueden visitar tus clientes."
        : "Terminá la publicación para obtener tu enlace.",
      label: publicUrl ? "Abrir web" : "Preparar web",
      color: "blue",
      badge: publicUrl ? "PUBLICADA" : "PENDIENTE",
      onClick: openPublicSite,
    },
  ].filter((action) => action.visible);

  return (
    <Stack spacing={{ xs: 1.35, sm: 2.1, md: 2.6 }} sx={{ minWidth: 0 }}>
      {error ? <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: 2.8, sm: 4.5 },
          p: { xs: 1.45, sm: 2.6, md: 3.25 },
          color: "#fff",
          background: "linear-gradient(125deg, #19242F 0%, #2C4056 54%, #6957E8 100%)",
          boxShadow: "0 18px 48px rgba(25,36,47,.14)",
        }}
      >
        <Box sx={{ position: "absolute", width: 210, height: 210, borderRadius: "50%", bgcolor: "rgba(255,200,87,.12)", right: -80, top: -115 }} />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction={{ xs: "row", md: "row" }} justifyContent="space-between" spacing={{ xs: 1.2, md: 2 }} alignItems="center">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={.65}>
                <AutoAwesomeRoundedIcon sx={{ color: BRAND.yellow, fontSize: { xs: 17, sm: 20 } }} />
                <Typography sx={{ color: "rgba(255,255,255,.65)", fontSize: { xs: ".61rem", sm: ".72rem" }, fontWeight: 950, letterSpacing: .65 }}>
                  {guidedStart ? "PUESTA EN MARCHA" : "PANEL OPERATIVO"}
                </Typography>
              </Stack>
              <Typography sx={{ mt: .38, fontSize: { xs: "1.2rem", sm: "1.95rem", md: "2.3rem" }, lineHeight: 1.08, fontWeight: 1000, letterSpacing: -.65 }}>
                {guidedStart
                  ? `Hola${user?.name ? ` ${user.name}` : ""}, terminemos de preparar ${activeProject?.name || "tu negocio"}`
                  : `Hola${user?.name ? ` ${user.name}` : ""}, así viene ${activeProject?.name || "tu negocio"}`}
              </Typography>
              <Typography sx={{ display: { xs: "none", sm: "block" }, mt: .65, maxWidth: 700, color: "rgba(255,255,255,.72)", fontSize: ".91rem", lineHeight: 1.5 }}>
                {guidedStart
                  ? "Te mostramos sólo lo esencial hasta que el negocio tenga su base real lista."
                  : "Actividad, tareas y accesos de uso diario en un mismo lugar. La guía sigue disponible cuando la necesites."}
              </Typography>
            </Box>

            <Box sx={{ width: { xs: 132, sm: 245, md: 285 }, flexShrink: 0 }}>
              <Typography sx={{ display: { xs: "none", sm: "block" }, mb: .5, color: "rgba(255,255,255,.62)", fontSize: ".67rem", fontWeight: 900 }}>PROYECTO ACTIVO</Typography>
              <Select
                fullWidth
                size="small"
                value={projectIdentifier(activeProject)}
                onChange={(event) => setActiveProject(event.target.value)}
                sx={{
                  minHeight: { xs: 38, sm: 40 },
                  bgcolor: "rgba(255,255,255,.11)",
                  color: "#fff",
                  borderRadius: 2.1,
                  fontSize: { xs: ".73rem", sm: ".86rem" },
                  fontWeight: 900,
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.2)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.42)" },
                  ".MuiSvgIcon-root": { color: "#fff" },
                  ".MuiSelect-select": { py: { xs: .8, sm: 1 } },
                }}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id || project._id || project.slug} value={projectIdentifier(project)}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
              <Stack direction="row" spacing={.6} useFlexGap flexWrap="wrap" sx={{ display: { xs: "none", sm: "flex" }, mt: .75 }}>
                <Chip size="small" label={billingAccessLabel(billingAccess)} sx={{ bgcolor: "rgba(255,255,255,.12)", color: "#fff", fontWeight: 850 }} />
                {modules.system ? <Chip size="small" label="Gestión activa" sx={{ bgcolor: "rgba(38,162,105,.24)", color: "#fff", fontWeight: 850 }} /> : null}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {operationallyLocked ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" startIcon={<WorkspacePremiumRoundedIcon />} onClick={() => navigate(subscribePath)} sx={{ fontWeight: 950 }}>
              Reactivar
            </Button>
          }
          sx={{ borderRadius: 3 }}
        >
          La página sigue disponible, pero las funciones operativas están pausadas.
        </Alert>
      ) : null}

      {!guidedStart ? (
        <Stack spacing={{ xs: 1.2, sm: 1.7 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
            <Box>
              <Typography sx={{ color: BRAND.dark, fontSize: { xs: "1rem", sm: "1.3rem" }, fontWeight: 1000 }}>Resumen de los últimos 7 días</Typography>
              <Typography sx={{ color: "rgba(25,36,47,.55)", fontSize: ".78rem" }}>Datos reales de actividad web y del panel para este proyecto.</Typography>
            </Box>
            {experience.helpAvailable ? (
              <Button
                size="small"
                startIcon={<HelpOutlineRoundedIcon />}
                onClick={() => setShowGuidance((current) => !current)}
                sx={{ alignSelf: { xs: "flex-start", sm: "center" }, textTransform: "none", borderRadius: 999, fontWeight: 950, color: BRAND.dark }}
              >
                {showGuidance ? "Ocultar guía" : "Necesito ayuda"}
              </Button>
            ) : null}
          </Stack>

          <Grid container spacing={{ xs: .85, sm: 1.2 }}>
            <Grid item xs={6} md={3}><DashboardMetric label="Actividad" value={activityMetricValue} helper="Eventos web y operativos" icon={<HistoryRoundedIcon sx={{ fontSize: 19 }} />} /></Grid>
            <Grid item xs={6} md={3}><DashboardMetric label="Visitantes" value={visitorMetricValue} helper="Personas únicas detectadas" icon={<PublicRoundedIcon sx={{ fontSize: 19 }} />} /></Grid>
            <Grid item xs={6} md={3}><DashboardMetric label="Alta intención" value={intentMetricValue} helper="Acciones comerciales" icon={<ArrowForwardRoundedIcon sx={{ fontSize: 19 }} />} /></Grid>
            <Grid item xs={6} md={3}><DashboardMetric label="Equipo activo" value={teamMetricValue} helper="Usuarios del panel" icon={<GroupRoundedIcon sx={{ fontSize: 19 }} />} /></Grid>
          </Grid>

          <Grid container spacing={{ xs: 1, sm: 1.4 }}>
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ height: "100%", p: { xs: 1.3, sm: 1.8 }, borderRadius: 3.5, border: "1px solid rgba(25,36,47,.08)" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Box>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 1000 }}>Actividad reciente</Typography>
                    <Typography sx={{ color: "rgba(25,36,47,.52)", fontSize: ".72rem" }}>Lo último que pasó en el negocio.</Typography>
                  </Box>
                  <Button size="small" onClick={() => go(`${projectBase}/actividad`)} sx={{ textTransform: "none", fontWeight: 900 }}>Ver historial</Button>
                </Stack>
                <Stack spacing={.65}>
                  {recentActivity.slice(0, 4).map((event) => (
                    <Box key={event.id || event._id} sx={{ p: 1, borderRadius: 2.2, bgcolor: "rgba(25,36,47,.025)" }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ color: BRAND.dark, fontSize: ".8rem", fontWeight: 900 }}>{event.title || "Actividad registrada"}</Typography>
                          <Typography noWrap sx={{ color: "rgba(25,36,47,.5)", fontSize: ".68rem" }}>{event.description || event.action || ""}</Typography>
                        </Box>
                        <Typography sx={{ flexShrink: 0, color: "rgba(25,36,47,.42)", fontSize: ".64rem", fontWeight: 800 }}>{relativeTime(event.occurredAt || event.createdAt)}</Typography>
                      </Stack>
                    </Box>
                  ))}
                  {!activityLoading && !recentActivity.length ? (
                    <Typography sx={{ py: 1.2, color: "rgba(25,36,47,.52)", fontSize: ".76rem" }}>Todavía no hay actividad registrada. Las primeras acciones van a aparecer acá.</Typography>
                  ) : null}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ height: "100%", p: { xs: 1.3, sm: 1.8 }, borderRadius: 3.5, border: "1px solid rgba(25,36,47,.08)" }}>
                <Stack direction="row" spacing={.7} alignItems="center" sx={{ mb: 1 }}>
                  <TaskAltRoundedIcon sx={{ color: operationalTasks.length ? BRAND.orange : BRAND.green, fontSize: 20 }} />
                  <Box>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 1000 }}>{operationalTasks.length ? "Tareas pendientes" : "Todo al día"}</Typography>
                    <Typography sx={{ color: "rgba(25,36,47,.52)", fontSize: ".72rem" }}>Pendientes detectados por el estado real del proyecto.</Typography>
                  </Box>
                </Stack>
                <Stack spacing={.7}>
                  {operationalTasks.map((task) => (
                    <ButtonBase key={task.id} onClick={() => go(task.path, { operational: ["/sistema", "/clientes", "/productos", "/agenda", "/pedidos"].some((segment) => task.path?.includes(segment)) })} sx={{ display: "block", width: "100%", textAlign: "left", p: 1, borderRadius: 2.2, bgcolor: "rgba(243,90,58,.045)" }}>
                      <Typography sx={{ color: BRAND.dark, fontSize: ".78rem", fontWeight: 950 }}>{task.label}</Typography>
                      <Typography sx={{ mt: .15, color: "rgba(25,36,47,.52)", fontSize: ".66rem", lineHeight: 1.35 }}>{task.description}</Typography>
                    </ButtonBase>
                  ))}
                  {!operationalTasks.length ? (
                    <Box sx={{ p: 1, borderRadius: 2.2, bgcolor: "rgba(38,162,105,.06)" }}>
                      <Typography sx={{ color: BRAND.dark, fontSize: ".78rem", fontWeight: 900 }}>No hay pasos obligatorios pendientes.</Typography>
                      <Typography sx={{ mt: .15, color: "rgba(25,36,47,.52)", fontSize: ".66rem" }}>Podés seguir operando normalmente y volver a la guía cuando quieras.</Typography>
                    </Box>
                  ) : null}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      ) : null}

      {readinessAvailable && (guidedStart || showGuidance) ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            border: "1px solid rgba(25,36,47,.09)",
            bgcolor: "#fff",
            p: { xs: 1.25, sm: 2, md: 2.35 },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={1}
            sx={{ mb: roadmapComplete ? 0 : { xs: 1, sm: 1.35 } }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={.75} alignItems="center">
                <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 20 }} />
                <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".98rem", sm: "1.25rem" }, fontWeight: 1000 }}>
                  {guidedStart
                    ? (roadmapComplete ? "Negocio listo para operar" : "Tu próximo paso")
                    : "Guía del proyecto"}
                </Typography>
                {readinessLoading ? (
                  <Chip size="small" label="Actualizando" variant="outlined" sx={{ height: 22, fontSize: ".62rem", fontWeight: 900 }} />
                ) : null}
              </Stack>
              <Typography sx={{ mt: .3, color: "rgba(25,36,47,.58)", fontSize: { xs: ".74rem", sm: ".82rem" }, lineHeight: 1.45 }}>
                {roadmapComplete
                  ? "Lo necesario para este proyecto ya tiene evidencia real. Podés seguir operando o mejorar cualquier área cuando quieras."
                  : guidedStart
                    ? "Mostramos sólo las próximas acciones pendientes según el estado real y las capacidades de este negocio. Podés abrir cualquiera; la primera es sólo la recomendación."
                    : "La ayuda no desaparece al pasar a modo operativo: acá podés retomar los pendientes que todavía merecen atención."}
              </Typography>
            </Box>
            <Stack direction="row" spacing={.7} alignItems="center" sx={{ flexShrink: 0 }}>
              <Chip
                size="small"
                label={`${Number(readiness?.completedCount || 0)}/${Number(readiness?.requiredCount || 0)} listos`}
                sx={{ bgcolor: "rgba(38,162,105,.09)", color: BRAND.green, fontWeight: 950 }}
              />
              <Chip
                size="small"
                label={`${Number(readiness?.score || 0)}%`}
                sx={{ bgcolor: "rgba(105,87,232,.09)", color: BRAND.purple, fontWeight: 950 }}
              />
            </Stack>
          </Stack>

          {!roadmapComplete ? (
            <Stack spacing={.75}>
              {pendingRoadmapSteps.map((step, index) => {
                const palette = COLOR_MAP[step.color] || COLOR_MAP.orange;
                const isNext = index === 0;
                return (
                  <ButtonBase
                    key={step.code}
                    onClick={step.onClick}
                    sx={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      borderRadius: 2.5,
                      "&:focus-visible": { boxShadow: `0 0 0 4px ${palette.soft}` },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: .85, sm: 1.15 },
                        p: { xs: 1, sm: 1.2 },
                        borderRadius: 2.5,
                        border: `1px solid ${isNext ? palette.border : "rgba(25,36,47,.08)"}`,
                        bgcolor: isNext ? palette.soft : "rgba(25,36,47,.018)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          bgcolor: isNext ? "#fff" : "rgba(25,36,47,.06)",
                          color: isNext ? palette.main : "rgba(25,36,47,.55)",
                          fontSize: ".72rem",
                          fontWeight: 1000,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={.65} alignItems="center" useFlexGap flexWrap="wrap">
                          <Typography sx={{ color: BRAND.dark, fontWeight: 1000, fontSize: { xs: ".82rem", sm: ".92rem" } }}>
                            {step.title}
                          </Typography>
                          {isNext ? (
                            <Chip size="small" label="RECOMENDADO" sx={{ height: 20, bgcolor: "#fff", color: palette.main, fontSize: ".56rem", fontWeight: 1000 }} />
                          ) : null}
                        </Stack>
                        <Typography sx={{ mt: .2, color: "rgba(25,36,47,.58)", fontSize: { xs: ".68rem", sm: ".76rem" }, lineHeight: 1.35 }}>
                          {step.description}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={.2} sx={{ flexShrink: 0 }}>
                        {step.disabled ? <LockClockRoundedIcon sx={{ fontSize: 18, color: "rgba(25,36,47,.45)" }} /> : <ArrowForwardRoundedIcon sx={{ fontSize: 20, color: palette.main }} />}
                        <Typography sx={{ display: { xs: "none", sm: "block" }, color: step.disabled ? "rgba(25,36,47,.48)" : palette.main, fontSize: ".64rem", fontWeight: 950 }}>
                          {step.disabled ? "Ver acceso" : step.label}
                        </Typography>
                      </Stack>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Stack>
          ) : null}
        </Paper>
      ) : null}

      {membershipsEnabled && !guidedStart ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            border: "1px solid rgba(105,87,232,.16)",
            bgcolor: "rgba(105,87,232,.035)",
            p: { xs: 1.15, sm: 2, md: 2.4 },
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: { xs: .9, sm: 1.25 }, px: { xs: .15, sm: .2 } }}>
            <Box>
              <Stack direction="row" spacing={.7} alignItems="center">
                <CardMembershipRoundedIcon sx={{ color: BRAND.purple, fontSize: 20 }} />
                <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".96rem", sm: "1.28rem" }, fontWeight: 1000 }}>Membresías en recepción</Typography>
              </Stack>
              <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.58)", fontSize: ".82rem" }}>
                Accesos priorizados por la capacidad activa. Catálogo, productos, caja y demás herramientas siguen disponibles abajo.
              </Typography>
            </Box>
            <Chip size="small" label="Membresías activas" sx={{ alignSelf: { xs: "flex-start", sm: "center" }, bgcolor: "rgba(105,87,232,.10)", color: BRAND.purple, fontWeight: 900 }} />
          </Stack>

          <Grid container spacing={{ xs: .9, sm: 1.45 }}>
            {membershipActions.map((action) => (
              <Grid item xs={6} md={3} key={action.key}>
                <ActionTile {...action} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : null}

      {(!guidedStart || !readinessAvailable) ? (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: 3, sm: 4 },
          border: "1px solid rgba(25,36,47,.08)",
          bgcolor: "#F9FAFD",
          p: { xs: 1.15, sm: 2, md: 2.4 },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="end" spacing={1} sx={{ mb: { xs: .9, sm: 1.25 }, px: { xs: .15, sm: .2 } }}>
          <Box>
            <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".96rem", sm: "1.28rem" }, fontWeight: 1000 }}>
              {guidedStart ? "Para empezar" : "Acciones rápidas"}
            </Typography>
            <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.58)", fontSize: ".82rem" }}>
              {guidedStart
                ? "Priorizamos estas herramientas según lo que elegiste al crear el negocio."
                : "Entrá directamente al formulario que necesitás."}
            </Typography>
          </Box>
          <Chip size="small" label={`${featuredActions.length} accesos`} variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" }, bgcolor: "#fff", fontWeight: 900 }} />
        </Stack>

        <Grid container spacing={{ xs: .9, sm: 1.45 }}>
          {featuredActions.map((action, index) => (
            <Grid
              item
              xs={index === 0 || (featuredActions.length % 2 === 0 && index === featuredActions.length - 1) ? 12 : 6}
              md={12 / Math.min(featuredActions.length, 4)}
              key={action.key}
            >
              <ActionTile {...action} featured />
            </Grid>
          ))}
        </Grid>
      </Paper>
      ) : null}

      {guidedStart && !showAllTools ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            border: "1px dashed rgba(25,36,47,.16)",
            bgcolor: "rgba(25,36,47,.025)",
            p: { xs: 1.4, sm: 1.8 },
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
            <Box>
              <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>El resto sigue disponible</Typography>
              <Typography sx={{ mt: .25, color: "rgba(25,36,47,.58)", fontSize: ".8rem" }}>
                No desactivamos funciones de tu prueba: simplemente dejamos primero lo que necesitás para arrancar.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowAllTools(true)}
              sx={{ flexShrink: 0, borderRadius: 999, textTransform: "none", fontWeight: 950, color: BRAND.dark, borderColor: "rgba(25,36,47,.2)" }}
            >
              Ver todas las herramientas
            </Button>
          </Stack>
        </Paper>
      ) : (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: { xs: .85, sm: 1.1 }, px: .2 }}>
          <Box>
            <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".96rem", sm: "1.25rem" }, fontWeight: 1000 }}>{guidedStart ? "Más herramientas" : "Todas las herramientas"}</Typography>
            <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.58)", fontSize: ".82rem" }}>
              {guidedStart ? "Todo lo incluido en tu prueba sigue accesible acá." : "Este inicio reemplaza la navegación compleja del proyecto."}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => guidedStart ? setShowAllTools(false) : navigate("/dashboard/proyectos")}
            sx={{ minWidth: 0, px: 1, textTransform: "none", color: "rgba(25,36,47,.58)", fontSize: { xs: ".68rem", sm: ".78rem" }, fontWeight: 950 }}
          >
            {guidedStart ? "Ocultar" : "Cambiar proyecto"}
          </Button>
        </Stack>

        <Grid container spacing={{ xs: .85, sm: 1.25 }}>
          {managementActions.map((action) => (
            <Grid item xs={6} sm={4} lg={3} key={action.key}>
              <ActionTile {...action} />
            </Grid>
          ))}
        </Grid>
      </Box>
      )}
    </Stack>
  );
}
